import * as PlaceType from "enums/PlaceType"

/**
 * @typedef {object} Advert
 * @property {NavigationType} navigationType
 * @property {object[]} navigationStyles
 * @property {Enum} departureType
 * @property {Address} departurePortAddress
 * @property {Address[]} departureAreaRegionals
 * @property {Address[]} departureAreaGlobals
 * @property {Enum} arrivalType
 * @property {Address} arrivalPortAddress
 * @property {Address} arrivalAreaRegional
 * @property {Address} arrivalAreaGlobal
 * @property {Stopover[]} stopovers
 *
 * @typedef {object} NavigationType
 * @property {number} id
 *
 * @typedef {object} Address
 * @property {Coords} coords
 *
 * @typedef {object} Coords
 * @property {number} lng
 * @property {number} lat
 *
 * @typedef {object} Enum
 * @property {string} enum_label
 * @property {string} enum_value
 *
 * @typedef {object} Stopover
 * @property {Address} location
 */

/**
 * @param {Advert} advert
 * @returns {Advert}
 */
export const normalize = (advert) => {
  const normalizedAdvert = {
    ...advert,
  }

  if (normalizedAdvert.navigationType?.id) {
    normalizedAdvert[`navigationStyles${normalizedAdvert.navigationType.id}`] =
      normalizedAdvert.navigationStyles
  }

  return normalizedAdvert
}

/**
 * @param {Advert} advert
 * @returns {?Coords}
 */
export const getDepartureCoords = (advert) => {
  switch (advert.departureType.enum_value) {
    case PlaceType.PORT:
      return isValidCoords(advert.departurePortAddress.coords)
        ? advert.departurePortAddress.coords
        : null

    case PlaceType.AREA_REGIONAL:
      return isValidCoords(advert.departureAreaRegionals[0].coords)
        ? advert.departureAreaRegionals[0].coords
        : null

    case PlaceType.AREA_GLOBAL:
      return isValidCoords(advert.departureAreaGlobals[0].coords)
        ? advert.departureAreaGlobals[0].coords
        : null

    default:
      return null
  }
}

/**
 * @param {Coords} coords
 * @returns {boolean}
 */
const isValidCoords = (coords) => {
  if (!coords) {
    return false
  }

  return Boolean(coords?.lng && coords?.lat)
}

/**
 * @param {Advert} advert
 * @returns {?Coords}
 */
export const getArrivalCoords = (advert) => {
  switch (advert.arrivalType.enum_value) {
    case PlaceType.PORT:
      return isValidCoords(advert.arrivalPortAddress?.coords)
        ? advert.arrivalPortAddress.coords
        : null

    case PlaceType.AREA_REGIONAL:
      return isValidCoords(advert.arrivalAreaRegional?.coords)
        ? advert.arrivalAreaRegional.coords
        : null

    case PlaceType.AREA_GLOBAL:
      return isValidCoords(advert.arrivalAreaGlobal?.coords)
        ? advert.arrivalAreaGlobal.coords
        : null

    default:
      console.warn(`Invalid arrivalType:`, advert.arrivalType)
      return null
  }
}

/**
 * @param {Advert} advert
 * @returns {Coords[]}
 */
export const getStopoversCoords = (advert) => {
  return advert.stopovers
    .map((stopover) => {
      if (isValidCoords(stopover.location.coords)) {
        return stopover.location.coords
      }

      return null
    })
    .filter(Boolean)
}

/**
 * @param {Advert} advert
 * @returns {Array<RegExp>}
 */
export const getSearchWords = (advert) => {
  return [advert.boat ? new RegExp(advert.boat.name, "i") : null].filter(
    Boolean,
  )
}
